/**
 * #750 — rank-aware privilege guards for manage-users.
 *
 * The function's only gate was "caller is admin or super_admin", which let an `admin`
 * set a `super_admin`'s password and sign in as them. These predicates add the missing
 * half: what the *target* holds, and what the caller may hand out.
 *
 * This covers the service-role path only. The browser can write public.user_roles
 * directly, and RLS is the control there — see the companion policy migration
 * (20261003181832_restrict_user_roles_super_admin_writes.sql).
 *
 * Pure, with no remote imports (only the sibling roles.ts), so src/test/unit imports the
 * real thing rather than a copy (same shape as meta-capi/meta-error.ts).
 */

import { type AppRole, isAppRole } from "./roles.ts";

// Re-exported so callers and tests get the rank model and the role check from one place.
// roles.ts (#719) owns the definition of a valid role; this file only ranks them.
export { isAppRole };

/**
 * Mirrors public.app_role. Higher outranks lower. The enum's declaration order is
 * ('admin','staff','viewer','super_admin') and is NOT a privilege order, so these are
 * written out rather than derived from it. Typed against roles.ts's AppRole, so a role
 * added there without a rank here fails to compile.
 */
const ROLE_RANK: Record<AppRole, number> = {
  viewer: 1,
  staff: 2,
  admin: 3,
  super_admin: 4,
};

/**
 * Rank is asymmetric on unrecognised roles, and the asymmetry is the whole point: an
 * unknown role must never make the caller stronger, and must never make the target look
 * weaker. One shared rankOf() cannot do both — used on the caller, "unknown → Infinity"
 * is fail-OPEN (a user holding `admin` plus some future enum value would outrank
 * everyone).
 *
 * Caller side: unrecognised roles are ignored. No roles → 0.
 */
export function rankOfCaller(roles: readonly string[]): number {
  let rank = 0;
  for (const r of roles) {
    if (isAppRole(r)) rank = Math.max(rank, ROLE_RANK[r]);
  }
  return rank;
}

/**
 * Target side: an unrecognised role ranks Infinity. If the DB enum grows a value this
 * table doesn't know, refuse to act on whoever holds it rather than assume it's
 * harmless. No roles → 0.
 */
export function rankOfTarget(roles: readonly string[]): number {
  if (roles.length === 0) return 0;
  return Math.max(
    ...roles.map((r) => (isAppRole(r) ? ROLE_RANK[r] : Number.POSITIVE_INFINITY)),
  );
}

/** A caller may act on a target of equal or lower rank. Peers may act on peers. */
export function canActOnTarget(
  callerRoles: readonly string[],
  targetRoles: readonly string[],
): boolean {
  return rankOfCaller(callerRoles) >= rankOfTarget(targetRoles);
}

/** A caller may grant only known roles, at or below its own rank. */
export function canGrantRole(callerRoles: readonly string[], role: unknown): boolean {
  if (!isAppRole(role)) return false;
  return ROLE_RANK[role] <= rankOfCaller(callerRoles);
}
